<template>
  <div :class="classes">
    <div class="consumption-from__arrow">
      <Arrow
        :type="arrowType"
        :angle="angle"
        ref="arrow"
        :disabled="arrowDisabled"
      />
    </div>
    <div class="consumption-from__value">
      <Wattage :value="animatedValue" />
    </div>
    <div class="consumption-from__title">{{ title }}</div>
  </div>
</template>

<script>
  import Arrow from '@/components/icons/ConsumptionArrow'
  import Wattage from '@/components/Wattage'
  import TweenLite from 'gsap'

  export default {
    name: 'consumption-from',
    components: {
      Arrow,
      Wattage,
    },
    data() {
      // Use decent defaults for the angles.
      let angle = 35
      if (this.type === 'consumption') {
        angle *= -1
      }

      return {
        angle: angle,
        animatedValue: 0,
      }
    },
    props: {
      type: {
        type: String,
        required: true,
      },
    },
    mounted() {
      this.animatedValue = this.rawValue
    },
    computed: {
      classes: function () {
        let classes = ['consumption-from', `consumption-from--${this.type}`]
        return classes.join(' ')
      },
      arrowType: function () {
        let result = this.type === 'community' ? 'positive' : 'negative'

        if (this.type === 'supplier') {
          const read = this.$store.getters.currentReads
          if (read.toSupplier > 0) {
            result = 'positive'
          }
        }

        return result
      },
      arrowDisabled: function () {
        return this.rawValue < 0.1
      },
      title: function () {
        if (this.type === 'community') {
          return this.str('home.communityConsumption')
        } else {
          return this.str('home.supplierConsumption')
        }
      },
      rawValue: function () {
        const read = this.$store.getters.currentReads
        if (this.type === 'community') {
          if (read.fromCommunity > 0) {
            return read.fromCommunity + read.communityConsumable
          } else {
            return read.toCommunity
          }
        } else {
          if (read.fromSupplier > 0) {
            return read.fromSupplier
          } else {
            return read.toSupplier
          }
        }
      },
      dotCoordinates: function () {
        return this.$store.getters.dotCoordinates
      },
    },
    methods: {
      updateArrowAngle: function (dotCoordinates) {
        const arrowCenter = this.getArrowCenterCoordinates()
        let dotCenter = {
          x: 0,
          y: 0,
        }
        let deltaX = 0
        let deltaY = 0

        if (this.type === 'community') {
          dotCenter.x = dotCoordinates.positiveX
          dotCenter.y = dotCoordinates.positiveY
          deltaX = arrowCenter.x - dotCenter.x
          deltaY = arrowCenter.y - dotCenter.y
        } else {
          dotCenter.x = dotCoordinates.negativeX
          dotCenter.y = dotCoordinates.negativeY
          deltaX = dotCenter.x - arrowCenter.x
          deltaY = dotCenter.y - arrowCenter.y
        }

        // Calculating the angle of a right triangle.
        // The triangle consists between the 2 center points and the deltas
        // between their x and y coordinates.
        // The angle in radians.
        this.angle = Math.atan(deltaY / deltaX)
        // Convert to degrees correct sign and round.
        this.angle = Math.round((180 / Math.PI) * this.angle)

        const read = this.$store.getters.currentReads

        // Adapt the arrow angles considering this.type and meter values.
        if (this.type === 'community') {
          if (read.toCommunity > 0) {
            this.angle -= 90
          } else {
            this.angle += 90
          }
        } else {
          if (read.toSupplier > 0) {
            this.angle += 90
          } else {
            this.angle -= 90
          }
        }
      },
      getArrowCenterCoordinates() {
        const rect = this.$refs.arrow.$el.getBoundingClientRect()
        const top = document.body.scrollTop
        const left = document.body.scrollLeft

        const result = {
          x: Math.round(left + rect.left + rect.width / 2),
          y: Math.round(top + rect.top + rect.height / 2),
        }
        return result
      },
    },
    watch: {
      dotCoordinates: function (newCoordinates) {
        this.updateArrowAngle(newCoordinates)
      },
      rawValue: function (newValue) {
        this.updateArrowAngle(this.dotCoordinates)
        TweenLite.to(this.$data, this.$store.getters.wattageTweenDuration, {
          animatedValue: newValue,
        })
      },
    },
  }
</script>

<style lang="scss">
  .consumption-from {
    text-align: center;

    // The arrows would otherwise overflow due to them being scaled inline svgs.
    overflow: hidden;

    &__arrow {
      transition: $transition-base;
      margin-bottom: 1vh;

      @media (min-height: 600px) {
        margin-bottom: 0;
      }

      @media (min-height: 1000px) {
        margin-bottom: 1vh;
      }

      @media (min-height: 1000px), (min-width: 700px) {
        .consumption-from--community & {
          margin-left: spacer(3);
        }

        .consumption-from--supplier & {
          margin-right: spacer(3);
        }
      }
    }

    &__title {
      line-height: $line-height-min;
      margin-top: spacer(0.1);
      font-size: $font-size-sm;

      @media (min-width: $bp-xs) {
        font-size: $font-size-base;
      }
    }

    &__value {
      margin-top: spacer(0.1);
      color: $supplier;

      .consumption-from--community & {
        color: $community;
      }
    }
  }
</style>
